import { useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'
import { Button, Dialog } from '@atoms'
import Badge from '@components/atoms/Badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/atoms/Tabs'
import { CopiableValue } from '@components/molecules/CopiableValue'
import Icon from '@components/utils/Icon'
import { toast } from '@stores/toastStore'
import { SupporterStatusBadge } from '@frontend/contexts/users_management/components/SupporterStatusBadge'

import {
  CallbackCard,
  SkipInSessionSwitch,
  callbackDueAt,
  callbackOwner,
  draftWhen,
  newCallbackDraft,
  ownersCallback,
  type AssignablePerson,
  type CallbackDraft,
} from '../components/ScheduleCallbackFields'
import { ContactsLog, type ContactEntry } from '../components/ContactsLog'
import { LogContactForm, type LoggedContact } from '../components/LogContactForm'

/**
 * Calling session supporter page, with callbacks (design-phase prototype)
 *
 * Today's calling session supporter page (CallingSessionSupporterShow on
 * staging) with one change: the Log contact dialog is the one the Calls page
 * uses, with #7415's outcome tiles and the schedule-callback fields.
 * Everything else mirrors the current page: CurrentCycleCard,
 * SupporterSummaryCard, SupporterTabs and SupporterPagination, with sample
 * data. The supporter details and notes tabs
 * are placeholders.
 *
 * For the version built on PR #7415's inline log-call form, see
 * CallingSessionV2CallbackPreview.
 */

// ---------- Sample data ----------

type Leader = { id: string; name: string }

const ME: Leader = { id: 'u1', name: 'Chiara Conti' }
const LEADERS: Leader[] = [ME, { id: 'u2', name: 'Marco Ferri' }, { id: 'u3', name: 'Sofia Greco' }]
// Who an admin can assign a callback to. Viewing as an admin, you are one.
const ASSIGNABLE: AssignablePerson[] = [
  { ...ME, kind: 'admin' },
  { id: 'u4', name: 'Alessandro Moretti', kind: 'admin' },
  ...LEADERS.filter((l) => l.id !== ME.id).map((l) => ({ ...l, kind: 'leader' as const })),
]
const GROUP = { name: 'Milano Centro' }
const ownerName = (draft: CallbackDraft) => ASSIGNABLE.find((p) => p.id === draft.assigneeId)?.name ?? 'Any leader'

const SUPPORTER = {
  firstName: 'Maria',
  lastName: 'Rossi',
  fullName: 'Maria Rossi',
  phone: '0412 345 678',
  email: 'maria.rossi@example.com',
  tags: ['Volunteer', 'Rally 2026'],
  lastContacted: '12 September 2026',
  supporterSince: '3 March 2024',
  location: 'Milano, MI, 20121',
  federalElectorate: 'Milano 1',
  note: 'Keen on door-knocking. Prefers calls after work.',
}

const CYCLE = { number: 2, available: 18 }
const POSITION = 3
const SESSION_SUPPORTERS = ['Luca Bianchi', 'Giulia Esposito', 'Maria Rossi', 'Paolo Romano', 'Elena Colombo']

const at = (daysFromToday: number, hour: number) => {
  const d = new Date()
  d.setDate(d.getDate() + daysFromToday)
  d.setHours(hour, 0, 0, 0)
  return d.toISOString()
}

const INITIAL_CONTACTS: ContactEntry[] = [
  { id: 'c1', outcome: 'no_answer', at: at(-17, 18), by: 'Marco Ferri', note: '' },
  {
    id: 'c2',
    outcome: 'meaningful_interaction',
    at: at(-40, 19),
    by: ME.name,
    note: 'Signed up for the doorknock. Asked about lifts.',
  },
]

// ---------- Page sections (sample-data copies of today's components) ----------

// CurrentCycleCard, mid-cycle with a call in progress.
function CurrentCycleCard() {
  return (
    <div className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-5">
      <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
        <Icon type="refresh-cw-04" className="tw:h-5 tw:w-5 tw:text-purple-600" />
        <h3 className="tw:text-base tw:font-semibold tw:text-gray-900">
          Cycle {CYCLE.number} - Today's calling session
        </h3>
      </div>
      <p className="tw:mb-4 tw:text-sm tw:text-gray-600">
        You have <span className="tw:font-semibold tw:text-primary-600">{CYCLE.available} supporters</span> available to
        call today.
      </p>
      <div className="tw:mb-4">
        <div className="tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:border tw:border-success-300 tw:bg-success-50/50 tw:px-4 tw:py-3">
          <div className="tw:flex tw:items-center tw:gap-2">
            <span className="tw:h-2 tw:w-2 tw:shrink-0 tw:rounded-full tw:bg-success-500" />
            <span className="tw:text-sm tw:font-medium tw:text-gray-900">On a call with {SUPPORTER.fullName}</span>
          </div>
          <Button size="sm" trailingIcon="arrow-circle-broken-right" disabled>
            Currently viewing {SUPPORTER.fullName}
          </Button>
        </div>
      </div>
      <div className="tw:flex tw:items-center tw:gap-3">
        <Button size="md" leadingIcon="play-circle" disabled>
          Start calling cycle
        </Button>
        <Button
          size="md"
          rank="secondary"
          leadingIcon="users-01"
          onClick={() => toast.info('Would list all supporters')}
        >
          View all supporters
        </Button>
      </div>
    </div>
  )
}

// SupporterSummaryCard.
function SupporterSummaryCard({
  openCallback,
  held,
  now,
  onLogContact,
  onHeldChange,
}: {
  openCallback: CallbackDraft | null
  // Skipped in this session until the callback is done
  held: boolean
  now: Date
  onLogContact: () => void
  onHeldChange: (held: boolean) => void
}) {
  const would = (what: string) => () => toast.info(`Would ${what}`)
  const rows = [
    { label: 'Last Contacted:', content: SUPPORTER.lastContacted },
    { label: 'Supporter Since:', content: SUPPORTER.supporterSince },
    { label: 'Location:', content: SUPPORTER.location },
    { label: 'Federal Electorate:', content: SUPPORTER.federalElectorate },
    { label: 'Background Note:', content: SUPPORTER.note, isNote: true },
  ]

  return (
    <div className="tw:mb-6 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-6">
      <div className="tw:mb-3 tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-baseline tw:gap-x-3 tw:gap-y-1">
          <h3 className="tw:m-0 tw:text-lg tw:font-semibold tw:text-gray-900">{SUPPORTER.fullName}</h3>
          <span className="tw:inline-flex tw:flex-wrap tw:gap-x-3 tw:gap-y-1 tw:text-sm tw:text-gray-500">
            <CopiableValue icon="phone-call-01" value={SUPPORTER.phone} copyLabel="Copy phone number" testId="phone" />
            <CopiableValue icon="mail-01" value={SUPPORTER.email} copyLabel="Copy email address" testId="email" />
          </span>
        </div>
        <span className="tw:min-w-fit">
          <SupporterStatusBadge
            status={held ? 'skipped_callback' : 'available'}
            unavailableReason={null}
            unavailableUntil={null}
            callbackDueAt={openCallback && callbackDueAt(openCallback).toISOString()}
            callbackFlexible={openCallback?.flexible}
          />
        </span>
      </div>

      <div className="tw:mb-3 tw:flex tw:flex-wrap tw:gap-1.5">
        {SUPPORTER.tags.map((tag) => (
          <Badge key={tag} color="gray" shape="rounded">
            {tag}
          </Badge>
        ))}
      </div>

      {openCallback && (
        <div className="tw:mb-4">
          <CallbackCard callback={openCallback} ownerName={ownerName(openCallback)} meId={ME.id} now={now}>
            <SkipInSessionSwitch firstName={SUPPORTER.firstName} checked={held} onCheckedChange={onHeldChange} />
          </CallbackCard>
        </div>
      )}

      <div className="tw:mb-6 tw:pt-2">
        <dl className="tw:grid tw:grid-cols-1 tw:items-center tw:gap-x-3 tw:gap-y-1 tw:sm:grid-cols-[max-content_1fr] tw:sm:gap-y-3">
          {rows.map(({ label, content, isNote }) => (
            <div key={label} className="tw:contents">
              <dt
                className={clsx(
                  'tw:flex tw:text-xs tw:font-bold tw:uppercase tw:text-gray-blue-500',
                  isNote ? 'tw:sm:items-start' : 'tw:items-center'
                )}
              >
                {label}
              </dt>
              <dd className="tw:m-0 tw:mb-3 tw:flex tw:text-sm tw:text-gray-900 tw:sm:mb-0">{content}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="tw:border-t tw:border-gray-200 tw:pt-6">
        <div className="tw:flex tw:flex-wrap tw:justify-between tw:gap-3">
          <div className="tw:flex tw:flex-wrap tw:gap-3">
            <Button rank="secondary" size="sm" leadingIcon="pencil-02" onClick={onLogContact}>
              Log contact
            </Button>
            <Button rank="secondary" size="sm" leadingIcon="message-chat-square" onClick={would('open the SMS dialog')}>
              SMS {SUPPORTER.firstName}
            </Button>
            <Button rank="secondary" size="sm" leadingIcon="mail-01" onClick={would('open the email dialog')}>
              Email {SUPPORTER.firstName}
            </Button>
          </div>
          <div className="tw:flex tw:flex-wrap tw:gap-3">
            <Button
              rank="link"
              size="sm"
              leadingIcon="arrow-circle-broken-right"
              disabled={held}
              onClick={would('go to the next supporter')}
            >
              Call next
            </Button>
            <Button
              rank="link"
              size="sm"
              leadingIcon="fast-forward"
              disabled={held}
              onClick={would('open the skip options')}
            >
              Skip for...
            </Button>
            <Button rank="link" size="sm" leadingIcon="user-x" onClick={would('open contact preferences')}>
              Manage contact preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

const TabPlaceholder = ({ children }: { children: string }) => (
  <div className="tw:rounded-lg tw:border tw:border-dashed tw:border-gray-300 tw:bg-gray-cool-50 tw:p-6 tw:text-center tw:text-sm tw:text-gray-500">
    {children}
  </div>
)

// ---------- Page ----------

const sampleCallback = (now: Date): CallbackDraft => ({
  ...newCallbackDraft(now, 'u2', 'Wants to hear about the Saturday doorknock.'),
  flexible: true,
})

// The call where the open callback was booked, so the contacts log shows where it came from.
const initialContacts = (now: Date): ContactEntry[] => [
  {
    id: 'c0',
    outcome: 'callback_requested',
    at: at(-1, 13),
    by: 'Marco Ferri',
    note: 'Was heading into a meeting. Asked us to ring back after work.',
    callback: sampleCallback(now),
  },
  ...INITIAL_CONTACTS,
]

const CallingSessionCallbackPreview = () => {
  const [now] = useState(() => new Date())
  const [contacts, setContacts] = useState<ContactEntry[]>(() => initialContacts(now))
  const [openCallback, setOpenCallback] = useState<CallbackDraft | null>(() => sampleCallback(now))
  const [held, setHeld] = useState(true)
  const [logOpen, setLogOpen] = useState(false)

  // Design tools
  const [showDesign, setShowDesign] = useState(false)
  const [role, setRole] = useState<'leader' | 'admin'>('leader')

  const saveContact = ({ outcome, note, callback, completeCallback }: LoggedContact) => {
    setLogOpen(false)
    setContacts((all) => [
      {
        id: `c-new-${all.length + 1}`,
        outcome,
        at: new Date().toISOString(),
        by: ME.name,
        note,
        callback: callback ?? undefined,
      },
      ...all,
    ])
    if (callback) {
      setOpenCallback(callback)
      setHeld(true)
      toast.success('Contact logged successfully', {
        description: `Callback ${openCallback ? 'moved to' : 'scheduled for'} ${draftWhen(callback, now)}.`,
      })
    } else if (completeCallback) {
      setOpenCallback(null)
      setHeld(false)
      toast.success('Contact logged successfully', { description: 'Callback marked complete.' })
    } else {
      toast.success('Contact logged successfully', {
        description: openCallback
          ? `${ownersCallback(callbackOwner(openCallback, ME.id), ownerName(openCallback))} for ${draftWhen(openCallback, now)} stays open.`
          : undefined,
      })
    }
  }

  const previous = SESSION_SUPPORTERS[POSITION - 2]
  const next = SESSION_SUPPORTERS[POSITION]

  return (
    <PageLayout.Root>
      <PageLayout.Header icon="user-square" heading={SUPPORTER.fullName} />
      <PageLayout.Body>
        <div className="tw:mb-5">
          <CurrentCycleCard />
        </div>

        <SupporterSummaryCard
          openCallback={openCallback}
          held={held}
          now={now}
          onLogContact={() => setLogOpen(true)}
          onHeldChange={(nextHeld) => {
            setHeld(nextHeld)
            toast.success(
              nextHeld
                ? `${SUPPORTER.fullName} won't be called until the callback is done`
                : `${SUPPORTER.fullName} is back in the queue`
            )
          }}
        />

        <Tabs defaultValue="contactsLog" urlKey="supporterTab" className="tw:flex tw:flex-col tw:gap-4">
          <TabsList className="tw:w-fit">
            <TabsTrigger value="supporterDetails">
              <Icon type="user-square" className="tw:mr-2" width={20} height={20} />
              Supporter details
            </TabsTrigger>
            <TabsTrigger value="contactsLog">
              <Icon type="mail-01" className="tw:mr-2" width={20} height={20} />
              Contacts log
            </TabsTrigger>
            <TabsTrigger value="notesLog">
              <Icon type="book-open-01" className="tw:mr-2" width={20} height={20} />
              Notes
            </TabsTrigger>
          </TabsList>
          <TabsContent value="supporterDetails">
            <TabPlaceholder>Supporter details tab, unchanged from today.</TabPlaceholder>
          </TabsContent>
          <TabsContent value="contactsLog">
            <ContactsLog contacts={contacts} ownerName={ownerName} meId={ME.id} now={now} />
          </TabsContent>
          <TabsContent value="notesLog">
            <TabPlaceholder>Notes tab, unchanged from today.</TabPlaceholder>
          </TabsContent>
        </Tabs>
      </PageLayout.Body>

      <PageLayout.Footer>
        {/* Stand-in for SupporterPagination */}
        <nav aria-label="Supporters in this session" className="tw:flex tw:w-full tw:items-center">
          <div className="tw:flex tw:flex-1 tw:justify-start">
            <Button
              rank="link"
              size="sm"
              leadingIcon="arrow-narrow-left"
              onClick={() => toast.info(`Would go to ${previous}`)}
            >
              Previous: {previous}
            </Button>
          </div>
          <ol className="tw:flex tw:items-center tw:justify-center tw:gap-4">
            {SESSION_SUPPORTERS.map((name, i) => (
              <li key={name}>
                <span
                  aria-current={i + 1 === POSITION ? 'page' : undefined}
                  title={name}
                  className={clsx(
                    'tw:inline-flex tw:h-7 tw:w-7 tw:items-center tw:justify-center tw:rounded-full tw:text-xs tw:font-bold',
                    i + 1 === POSITION ? 'tw:bg-primary-700 tw:text-white' : 'tw:bg-gray-100 tw:text-black'
                  )}
                >
                  {i + 1}
                </span>
              </li>
            ))}
          </ol>
          <div className="tw:flex tw:flex-1 tw:justify-end">
            <Button
              rank="link"
              size="sm"
              trailingIcon="arrow-narrow-right"
              onClick={() => toast.info(`Would go to ${next}`)}
            >
              Next: {next}
            </Button>
          </div>
        </nav>
      </PageLayout.Footer>

      <Dialog.Root open={logOpen} onOpenChange={setLogOpen}>
        <Dialog.Content size="xl" scroll>
          {logOpen && (
            <LogContactForm
              key={role}
              supporter={SUPPORTER}
              openCallback={openCallback}
              ownerName={openCallback ? ownerName(openCallback) : ''}
              meId={ME.id}
              now={now}
              inSession
              assign={
                role === 'admin'
                  ? { mode: 'admin', people: ASSIGNABLE, meId: ME.id, groupName: GROUP.name }
                  : { mode: 'leader', meId: ME.id, meName: ME.name, groupName: GROUP.name }
              }
              onSave={saveContact}
            />
          )}
        </Dialog.Content>
      </Dialog.Root>

      {/* Design tools: dev-only floating overlay, portaled to body, default closed */}
      {createPortal(
        <>
          <button
            onClick={() => setShowDesign((s) => !s)}
            className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
          >
            <Icon type="eye" className="tw:h-4 tw:w-4" />
            {showDesign ? 'Hide design tools' : 'Design tools'}
          </button>
          {showDesign && (
            <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(720px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
              <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" />
                <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                <button
                  onClick={() => setShowDesign(false)}
                  aria-label="Hide design tools"
                  className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600"
                >
                  <Icon type="x-close" className="tw:h-4 tw:w-4" />
                </button>
              </div>
              <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                This panel is purely for exploring the design and is not part of the product.
                <br />
                Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
              </p>
              <div className="tw:flex tw:flex-wrap tw:gap-8">
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">
                    Viewing as
                  </span>
                  <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
                    {(
                      [
                        ['leader', 'Leader'],
                        ['admin', 'Admin'],
                      ] as const
                    ).map(([value, label], i) => (
                      <button
                        key={value}
                        onClick={() => setRole(value)}
                        aria-pressed={role === value}
                        className={clsx(
                          'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
                          i > 0 && 'tw:border-l tw:border-gray-300',
                          role === value
                            ? 'tw:bg-primary-50 tw:text-primary-700'
                            : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>,
        document.body
      )}
    </PageLayout.Root>
  )
}

export default CallingSessionCallbackPreview
